import React, { useCallback, useRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { ConnectDragSource } from 'react-dnd';
import { Takeaway, CardSize } from '@annote/types';
import { text, colors, dimensions } from '@annote/core/styles';
import { ProseEditor } from 'src/components/editor/ProseEditor';
import { useBrainContext } from 'src/context/BrainContext';
import { ShownIdsContext } from 'src/context/ShownContext';
import { useDndRef } from 'src/helpers/useDndRef';

export const TITLE_PLACEHOLDER = 'Untitled Note';
const VALUE_PLACEHOLDER = 'Describe what you have found out';
const FOCUSED_PLACEHOLDER = ` Press '#' to link to a note or article`;

// TODO this wrapper could actually go in the card element?
const Wrapper = styled.div<{ size: CardSize; inPanel: boolean }>`
  display: flex;
  flex-direction: column;
  /* pointer-events: none; */
  user-select: none;
  position: relative;
  width: ${({ size, inPanel }) =>
    inPanel ? '100%' : `${size === 'LARGE' ? dimensions.oneInchRem * 8.5 : dimensions.oneInchRem * 5}rem`};
  ${({ size, inPanel }) =>
    !inPanel && `max-height: ${size === 'LARGE' ? dimensions.oneInchRem * 11 : dimensions.oneInchRem * 8.33}rem;`}
  overflow: ${({ inPanel }) => (inPanel ? 'visible' : 'auto')};
  ${({ inPanel }) => inPanel && `height: 100%;`}
  box-sizing: border-box;
`;

const TitleRow = styled.div`
  font-family: ${text.family.primary};
  font-weight: ${text.weight.bold};
  display: flex;
  flex-direction: row;
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid ${colors.lines.indexRed};
  pointer-events: auto;
  width: 100%;
  box-sizing: border-box;
`;

const Title = styled.textarea`
  font-family: ${text.family.primary};
  font-weight: ${text.weight.medium};
  font-size: ${text.size.primary};
  line-height: ${text.height.header};
  color: ${colors.text.header};
  flex: 1;
  resize: 'vertical';

  border: none;
  resize: none;
  overflow: hidden;
  outline: none;
  background: transparent;
  padding: 0;
  margin: 0;

  &:empty::before {
    content: attr(placeholder);
    color: ${colors.text.header};
  }
`;

// Simple div to mark the draggable area
const DraggableArea = styled.div`
  cursor: pointer;
`;

const ValueRow = styled.div<{ size: CardSize; inPanel?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 16px;
  padding-top: 0;
  padding-bottom: 0px;
  pointer-events: auto;
  font-weight: ${text.weight.regular};
  font-size: ${({ size }) => (size === 'LARGE' ? '16px' : '14px')};
  line-height: ${({ size }) => (size === 'LARGE' ? '24px' : '20px')};
  color: ${colors.text.primary};
  width: 100%;
  box-sizing: border-box;

  .ProseMirror {
    flex: 1;
    min-height: ${({ size, inPanel }) => {
      if (inPanel) return '200px';
      return size === 'LARGE' ? `${dimensions.oneInchRem * 10}rem` : `${dimensions.oneInchRem * 2.5}rem`;
    }};
    width: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const TakeawayCard: React.FC<{
  takeaway: Takeaway;
  dragRef?: ConnectDragSource;
  readOnly?: boolean;
  forceSize?: CardSize;
  inPanel?: boolean;
}> = ({ takeaway, dragRef, readOnly = false, forceSize, inPanel = false }) => {
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const { updateNotes } = useBrainContext();

  const { shownSizes } = useContext(ShownIdsContext);
  const size = forceSize || (readOnly ? 'SMALL' : shownSizes[takeaway.id] || 'SMALL');

  const debouncedUpdateNotes = useCallback(debounce(updateNotes, 500), [updateNotes]);

  const setRef = dragRef ? useDndRef(dragRef) : undefined;

  const sizeTitle = useCallback(() => {
    if (!titleRef.current) return;
    titleRef.current.style.height = 'inherit';
    titleRef.current.style.height = `${titleRef.current.scrollHeight}px`;
  }, [titleRef]);

  const updateTitle = useCallback(() => {
    if (!titleRef.current) return;
    const title = titleRef.current.value;
    sizeTitle();

    if (title === takeaway.title) return;
    debouncedUpdateNotes([{ ...takeaway, title }]);
  }, [takeaway, titleRef, debouncedUpdateNotes]);

  // resize title on mount
  useEffect(() => {
    sizeTitle();
  }, [sizeTitle]);

  // update title ref if takeaway.title changes
  useEffect(() => {
    if (!titleRef.current) return;
    titleRef.current.value = takeaway.title;
  }, [takeaway.title]);

  return (
    <Wrapper size={size} inPanel={inPanel}>
      <DraggableArea ref={setRef}>
        <TitleRow>
          <Title
            ref={titleRef}
            defaultValue={takeaway.title}
            contentEditable={!readOnly}
            rows={1}
            onChange={updateTitle}
            placeholder={TITLE_PLACEHOLDER}
            suppressContentEditableWarning
          />
        </TitleRow>
      </DraggableArea>
      <ValueRow size={size} inPanel={inPanel} className="nodrag">
        <ProseEditor
          key={`${takeaway.id}-${inPanel ? 'panel' : 'surface'}`}
          note={takeaway}
          placeholder={VALUE_PLACEHOLDER}
          focusedPlaceholder={FOCUSED_PLACEHOLDER}
          isReadOnly={readOnly}
          isInline={false}
        />
      </ValueRow>
    </Wrapper>
  );
};

export default TakeawayCard;
